import { Route } from "react-router-dom";
import { lazy } from "react";

// Lazy load all public routes for code splitting
const Playbook = lazy(() => import("@/pages/Playbook"));
const FitCheck = lazy(() => import("@/pages/FitCheck"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const Prework = lazy(() => import("@/pages/Prework"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Contact = lazy(() => import("@/pages/Contact"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Refund = lazy(() => import("@/pages/Refund"));
const LeadershipArticles = lazy(() => import("@/pages/LeadershipArticles"));
const LeadershipArticle = lazy(() => import("@/pages/LeadershipArticle"));
const LeadershipArticlesDb = lazy(() => import("@/pages/LeadershipArticlesDb"));
const LeadershipArticleDb = lazy(() => import("@/pages/LeadershipArticleDb"));
const CareerGuides = lazy(() => import("@/pages/CareerGuides"));
const ICToStaff = lazy(() => import("@/pages/career-guides/ICToStaff"));
const FirstTimeManager = lazy(() => import("@/pages/career-guides/FirstTimeManager"));
const DirectorPlus = lazy(() => import("@/pages/career-guides/DirectorPlus"));
const Newsletter = lazy(() => import("@/pages/Newsletter"));
const About = lazy(() => import("@/pages/About"));
const Resources = lazy(() => import("@/pages/Resources"));
const Infographics = lazy(() => import("@/pages/Infographics"));
const CompensationPlaybook = lazy(() => import("@/pages/CompensationPlaybook"));
const SurvivalKit = lazy(() => import("@/pages/SurvivalKit"));
const CareerEdgeBlueprint = lazy(() => import("@/pages/CareerEdgeBlueprint"));
const CareerEdgeEnrollment = lazy(() => import("@/pages/CareerEdgeEnrollment"));
const CareerEdgeCheckout = lazy(() => import("@/pages/CareerEdgeCheckout"));
const PortalSession = lazy(() => import("@/pages/PortalSession"));
const Courses = lazy(() => import("@/pages/Courses"));
const AIFutureWorkToolkit = lazy(() => import("@/pages/AIFutureWorkToolkit"));
const ToolkitVerification = lazy(() => import("@/pages/ToolkitVerification"));
const ToolkitAccess = lazy(() => import("@/pages/ToolkitAccess"));
const NewsletterVerification = lazy(() => import("@/pages/NewsletterVerification"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const EmailTrackingRedirect = lazy(() => import("@/pages/EmailTrackingRedirect"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));

// Salary Edge (Lead Magnet for Compensation Playbook)
const SalaryEdgeLanding = lazy(() => import("@/pages/salary-edge/Landing"));
const SalaryEdgeSurvey = lazy(() => import("@/pages/salary-edge/Survey"));
const SalaryEdgeReport = lazy(() => import("@/pages/salary-edge/Report"));

// Archetype Insight (Leadership Assessment)
const ArchetypeInsightLanding = lazy(() => import("@/pages/archetype-insight/Landing"));
const ArchetypeInsightAssessment = lazy(() => import("@/pages/archetype-insight/Assessment"));
const ArchetypeInsightResult = lazy(() => import("@/pages/archetype-insight/Result"));
const ArchetypeInsightUnlock = lazy(() => import("@/pages/archetype-insight/Unlock"));

// AI Career Compass (Career Trajectory Assessment)
const AICareerCompass = lazy(() => import("@/pages/ai-career-compass/Index"));
const AICareerCompassResult = lazy(() => import("@/pages/ai-career-compass/Result"));

export const publicRoutes = (
  <>
    <Route path="/playbook" element={<Playbook />} />
    <Route path="/fit-check" element={<FitCheck />} />
    <Route path="/checkout" element={<Checkout />} />
    <Route path="/checkout/success" element={<CheckoutSuccess />} />
    <Route path="/welcome" element={<Welcome />} />
    <Route path="/prework" element={<Prework />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/refund" element={<Refund />} />
    <Route path="/leadership-articles" element={<LeadershipArticlesDb />} />
    <Route path="/leadership-articles/:slug" element={<LeadershipArticleDb />} />
    <Route path="/leadership-articles2" element={<LeadershipArticles />} />
    <Route path="/leadership-articles2/:slug" element={<LeadershipArticle />} />
    <Route path="/career-guides" element={<CareerGuides />} />
    <Route path="/career-guides/ic-to-staff" element={<ICToStaff />} />
    <Route path="/career-guides/first-time-manager" element={<FirstTimeManager />} />
    <Route path="/career-guides/director-plus" element={<DirectorPlus />} />
    <Route path="/newsletter" element={<Newsletter />} />
    <Route path="/about" element={<About />} />
    <Route path="/resources" element={<Resources />} />
    <Route path="/infographics" element={<Infographics />} />
    <Route path="/compensation-playbook" element={<CompensationPlaybook />} />
    <Route path="/survival-kit" element={<SurvivalKit />} />
    <Route path="/courses" element={<Courses />} />
    <Route path="/career-edge-blueprint" element={<CareerEdgeBlueprint />} />
    <Route path="/career-edge-enroll" element={<CareerEdgeEnrollment />} />
    <Route path="/career-edge-checkout" element={<CareerEdgeCheckout />} />
    <Route path="/sessions/:sessionId" element={<PortalSession />} />
    <Route path="/ai-future-work-toolkit" element={<AIFutureWorkToolkit />} />
    <Route path="/toolkit/verify" element={<ToolkitVerification />} />
    <Route path="/toolkit/access" element={<ToolkitAccess />} />
    <Route path="/newsletter/verify" element={<NewsletterVerification />} />
    <Route path="/unsubscribe" element={<Unsubscribe />} />
    <Route path="/track/click/:logId" element={<EmailTrackingRedirect />} />
    <Route path="/track/open/:logId" element={<EmailTrackingRedirect />} />
    <Route path="/portal/track/click/:logId" element={<EmailTrackingRedirect />} />
    <Route path="/portal/track/open/:logId" element={<EmailTrackingRedirect />} />

    {/* Salary Edge Routes */}
    <Route path="/salary-edge" element={<SalaryEdgeLanding />} />
    <Route path="/salary-edge/survey" element={<SalaryEdgeSurvey />} />
    <Route path="/salary-edge/report/:reportId" element={<SalaryEdgeReport />} />

    {/* Archetype Insight Routes */}
    <Route path="/archetype-insight" element={<ArchetypeInsightLanding />} />
    <Route path="/archetype-insight/assessment" element={<ArchetypeInsightAssessment />} />
    <Route path="/archetype-insight/unlock" element={<ArchetypeInsightUnlock />} />
    <Route path="/archetype-insight/result" element={<ArchetypeInsightResult />} />

    {/* AI Career Compass Routes */}
    <Route path="/ai-career-compass" element={<AICareerCompass />} />
    <Route path="/ai-career-compass/result/:trajectoryId" element={<AICareerCompassResult />} />
  </>
);
